import { type DashboardDeployPayload } from "@repo/schema";
import z from "zod";

export const deployDashboardResponseSchema = z.object({
  dashboard_url: z.string(),
  configs_count: z.number(),
});
export type DeployDashboardResponse = z.infer<
  typeof deployDashboardResponseSchema
>;

/**
 * One entry in the multipart deploy body.
 *
 * Derived from `DashboardDeployPayload` rather than hand-written. The whole
 * wire format used to be duplicated here as ~90 lines of TS types, which is
 * how it came to omit the quantifier fields from `DeployEvalLimitItem` while
 * the code sent them anyway — the camera defaults that trio to
 * EXACT/PERCENT/100, so a genuine omission would have rewritten every limit
 * silently. It now has one definition, in `@repo/schema`, shared with the API
 * that produces it.
 */
export type DeployConfigEntry = Pick<
  DashboardDeployPayload,
  "dashboard_config" | "nn_config"
>;

// ========== Camera-side pull deployment ==========

/**
 * What the camera reports while it fetches and deploys.
 *
 * Mirrors `DeploymentPhase` in `robopipe_api/models/dashboard/deployment.py`.
 */
export type DeploymentPhase =
  | "queued"
  | "downloading"
  | "deploying"
  | "completed"
  | "failed";

export type DeploymentModelProgress = {
  index: number;
  output_id: number;
  bytes_downloaded: number;
  size_bytes: number | null;
  /** True when the archive was already on the camera and nothing transferred. */
  cached: boolean;
};

export type DeploymentStatus = {
  deployment_id: string;
  phase: DeploymentPhase;
  configs_count: number;
  models: DeploymentModelProgress[];
  error: string | null;
};

const TERMINAL_PHASES = new Set<DeploymentPhase>(["completed", "failed"]);

export const isDeploymentTerminal = (phase: DeploymentPhase): boolean =>
  TERMINAL_PHASES.has(phase);

/**
 * Fraction of the transfer that is done, 0..1, or null when the camera could
 * not size the artifacts (so a determinate bar would be a lie).
 */
export const deploymentProgress = (
  status: DeploymentStatus,
): number | null => {
  const total = status.models.reduce(
    (sum, model) => sum + (model.size_bytes ?? 0),
    0,
  );

  if (total === 0) return null;

  const done = status.models.reduce(
    (sum, model) => sum + model.bytes_downloaded,
    0,
  );

  return Math.min(1, done / total);
};
